import { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { ACCENT, BORDER, EASE_SPRING, FONT_MONO, MUTED, TEXT_PRIMARY } from '../../tokens';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

interface Schedule {
  days: readonly number[];
  label: string;
}

const SEQ: readonly Schedule[] = [
  { days: [1, 2, 3, 4, 5], label: 'LinkedIn, every weekday' },
  { days: [1, 2, 3, 4, 5], label: 'Instagram and Facebook, every weekday' },
];

const FALLBACK: Schedule = { days: [], label: '' };

/** Day-selector animation: picks days, writes a label, then stamps "Saved". */
export function SchedulerCard() {
  const reduced = usePrefersReducedMotion();
  const first = SEQ[0] ?? FALLBACK;

  const [active, setActive] = useState<readonly number[]>(reduced ? first.days : []);
  const [label, setLabel] = useState(reduced ? first.label : '');
  const [saved, setSaved] = useState(reduced);
  const [seqIndex, setSeqIndex] = useState(0);

  useEffect(() => {
    if (reduced) return;

    const step = SEQ[seqIndex] ?? FALLBACK;

    setActive([]);
    setLabel('');
    setSaved(false);

    const timers = [
      setTimeout(() => setActive(step.days), 300),
      setTimeout(() => setLabel(step.label), 800),
      setTimeout(() => setSaved(true), 1300),
      setTimeout(() => setSeqIndex((i) => (i + 1) % SEQ.length), 3200),
    ];

    return () => timers.forEach(clearTimeout);
  }, [seqIndex, reduced]);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: '0.62rem', color: MUTED, letterSpacing: '0.1em', marginBottom: 12 }}>
        CONTENT CALENDAR
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 5, marginBottom: 14 }}>
        {DAYS.map((day, i) => {
          const on = active.includes(i);
          return (
            <div
              key={i}
              style={{
                height: 38,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_MONO,
                fontSize: '0.72rem',
                fontWeight: 600,
                background: on ? ACCENT : 'rgba(255,255,255,0.05)',
                color: on ? '#fff' : '#444',
                transform: on ? 'scale(1.1)' : 'scale(1)',
                transition: `all 0.5s ${EASE_SPRING}`,
              }}
            >
              {day}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: 10,
          padding: '10px 14px',
          border: `1px solid ${BORDER}`,
        }}
      >
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: '0.76rem',
            color: label ? TEXT_PRIMARY : '#333',
            transition: 'color 0.4s',
          }}
        >
          {label || 'Configuring...'}
        </span>
        <span
          style={{
            fontSize: '0.68rem',
            fontWeight: 700,
            padding: '4px 11px',
            borderRadius: 6,
            background: saved ? ACCENT : 'rgba(255,107,0,0.1)',
            color: saved ? '#fff' : ACCENT,
            transition: `all 0.35s ${EASE_SPRING}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {saved ? '✓ Saved' : 'Save'}
        </span>
      </div>
    </div>
  );
}
